import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Tooltip } from "@mui/material";

const DEFAULT_YEAR_PADDING = 2;
const DYNAMIC_YEAR_PADDING = 1;

function DynamicTimeSelector({ intervals, setIntervals }) {
  const addInterval = () => {
    setIntervals([
      ...intervals,
      { id: uuidv4(), from: '', to: '', monitoring: false },
    ]);
  };

  const removeInterval = (idToRemove) => {
    if (intervals.length <= 1) return;
    setIntervals(intervals.filter(interval => interval.id !== idToRemove));
  };

  const updateInterval = (idToUpdate, fieldName, value) => {
    setIntervals(intervals.map(interval => {
      if (interval.id === idToUpdate) {
        const updatedInterval = { ...interval, [fieldName]: value };
        if (fieldName === 'from' && updatedInterval.to && new Date(updatedInterval.from + 'T00:00:00Z') > new Date(updatedInterval.to + 'T00:00:00Z')) {
          updatedInterval.to = '';
        }
        return updatedInterval;
      }
      return interval;
    }));
  };

  const isScheduleButtonEnabled = (interval) => {
    const { from, to } = interval;
    if (!from || !to) return false;

    const fromDate = new Date(from);
    const toDate = new Date(to);
    const now = new Date();

    return fromDate < toDate && toDate > now;
  };

  const formatDateForInput = (dateString) => {
    if (dateString && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    return '';
  };

  const parseDate = (dateString) => {
    if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return null;
    try {
      const date = new Date(dateString + 'T00:00:00Z');
      if (isNaN(date.getTime())) return null;
      return date.getTime();
    } catch (e) { return null; }
  };

  const timelineData = useMemo(() => {
    let minTs = null;
    let maxTs = null;

    intervals.forEach(interval => {
      const start = parseDate(interval.from);
      const end = parseDate(interval.to);
      if (start !== null && end !== null && end >= start) {
        if (minTs === null || start < minTs) {
          minTs = start;
        }
        if (maxTs === null || end > maxTs) {
          maxTs = end;
        }
      }
    });

    const today = new Date();
    const todayTs = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

    let timelineStartTs;
    let timelineEndTs;
    const yearInMs = 365.25 * 24 * 60 * 60 * 1000;

    if (minTs === null || maxTs === null) {
      timelineStartTs = todayTs - DEFAULT_YEAR_PADDING * yearInMs;
      timelineEndTs = todayTs + DEFAULT_YEAR_PADDING * yearInMs;
    } else {
      const overallMin = Math.min(minTs, todayTs);
      const overallMax = Math.max(maxTs, todayTs);

      timelineStartTs = overallMin - DYNAMIC_YEAR_PADDING * yearInMs;
      timelineEndTs = overallMax + DYNAMIC_YEAR_PADDING * yearInMs;
    }

    const totalTimelineDuration = timelineEndTs - timelineStartTs;

    const startYear = new Date(timelineStartTs).getUTCFullYear();
    const endYear = new Date(timelineEndTs).getUTCFullYear();
    const yearMarkers = [];
    const rangeYears = endYear - startYear;

    let yearStep = 1;
    if (rangeYears > 50) yearStep = 10;
    else if (rangeYears > 15) yearStep = 5;
    else if (rangeYears > 5) yearStep = 2;

    if (rangeYears / yearStep > 20) {
      yearStep = Math.ceil(rangeYears / 15);
      if (yearStep > 2 && yearStep < 5) yearStep = 5;
      else if (yearStep > 5 && yearStep < 10) yearStep = 10;
      else if (yearStep > 10 && yearStep < 20) yearStep = 20;
      else if (yearStep > 20 && yearStep < 50) yearStep = 50;
      else if (yearStep > 50) yearStep = 100;
    }

    const firstMarkerYear = Math.ceil(startYear / yearStep) * yearStep;

    for (let year = firstMarkerYear; year <= endYear; year += yearStep) {
      const yearTs = Date.UTC(year, 0, 1);
      if (yearTs >= timelineStartTs && yearTs <= timelineEndTs && totalTimelineDuration > 0) {
        const positionPercent = ((yearTs - timelineStartTs) / totalTimelineDuration) * 100;
        if (positionPercent >= 1 && positionPercent <= 99) {
          yearMarkers.push({ year, positionPercent });
        }
      }
    }

    let todayPositionPercent = null;
    if (todayTs >= timelineStartTs && todayTs <= timelineEndTs && totalTimelineDuration > 0) {
      todayPositionPercent = ((todayTs - timelineStartTs) / totalTimelineDuration) * 100;
    }

    return {
      timelineStartTs,
      timelineEndTs,
      totalTimelineDuration,
      yearMarkers,
      todayPositionPercent,
      todayTs
    };
  }, [intervals]);

  return (
    <div className="time-interval-selector-container">
      <div className="columns-wrapper">
        <div className="forms-column">
          {intervals.map((interval, index) => (
            <div key={interval.id} className="interval-form-card" style={{ marginBottom: '20px' }}>
              <div className="interval-form-header">
                <b>Time Interval {index + 1}</b>
                {intervals.length > 1 && (
                  <button
                    onClick={() => removeInterval(interval.id)}
                    className="remove-button"
                    aria-label={`Remove Time Interval ${index + 1}`}
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="interval-form-body">
                <div className="date-inputs">
                  <label>
                    From
                    <input
                      type="date"
                      name="from"
                      value={formatDateForInput(interval.from)}
                      onChange={(e) => updateInterval(interval.id, 'from', e.target.value)}
                      className="date-input"
                      aria-label={`Interval ${index + 1} Start Date`}
                    />
                  </label>
                  <label>
                    To
                    <input
                      type="date"
                      name="to"
                      value={formatDateForInput(interval.to)}
                      onChange={(e) => updateInterval(interval.id, 'to', e.target.value)}
                      className="date-input"
                      min={interval.from || undefined}
                      aria-label={`Interval ${index + 1} End Date`}
                      disabled={!interval.from}
                    />
                  </label>
                </div>
                <div className="monitoring-toggle">
                  <label className="switch" aria-label={`Interval ${index + 1} Monitoring Toggle`}>
                    <input
                      type="checkbox"
                      name="monitoring"
                      checked={interval.monitoring}
                      onChange={(e) => {
                        updateInterval(interval.id, 'monitoring', e.target.checked);
                      }}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span>Monitoring <span className="monitoring-subtext">(Regular tracking over a period)</span></span>
                </div>
                {interval.monitoring && (
                  <Tooltip
                    title={
                      !isScheduleButtonEnabled(interval)
                        ? "Requirements:\n1. Both 'From' and 'To' dates must be set.\n2. 'From' date must be before 'To' date.\n3. 'To' date must be in the future."
                        : ""
                    }
                    placement="top"
                    arrow>
                    <div className="wrapper-time" style={{ width: 'fit-content' }}>
                      <label htmlFor="from-date">Every</label>
                      <input
                        type="number"
                        defaultValue={interval.recurrenceInterval}
                        style={{ width: 'auto' }}
                        disabled={!isScheduleButtonEnabled(interval)}
                        min="1"
                        onChange={(e) => updateInterval(interval.id, 'recurrenceInterval', e.target.value)}
                      />
                      <select
                        value={interval.recurrenceType}
                        style={{ width: 'auto' }}
                        onChange={(e) => updateInterval(interval.id, 'recurrenceType', e.target.value)}
                        disabled={!isScheduleButtonEnabled(interval)}>
                        <option value="Days">Days</option>
                        <option value="Weeks">Weeks</option>
                        <option value="Months">Months</option>
                      </select>
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
          <button onClick={addInterval} className="button-link" style={{ color: '#f0941d', textAlign: 'left', marginTop: '-5px', alignSelf: 'flex-start' }}>
            + Add another time interval
          </button>
        </div>

        <div className="timeline-column">
          <div className="timeline-preview-card">
            <div className="timeline-graphic">
              <div className="timeline-axis"></div>

              {timelineData.yearMarkers.map(marker => (
                <div
                  key={marker.year}
                  className="timeline-marker"
                  style={{ left: `${marker.positionPercent}%` }}
                >
                  <div className="timeline-tick"></div>
                  <span>{marker.year}</span>
                </div>
              ))}

              {timelineData.todayPositionPercent !== null && (
                <div
                  className="timeline-marker today-marker"
                  style={{ left: `${timelineData.todayPositionPercent}%` }}
                >
                  <div className="timeline-tick"></div>
                  <span>Today</span>
                </div>
              )}
            </div>
            <div className="timeline-intervals">
              {timelineData.totalTimelineDuration > 0 && intervals.map((interval, index) => {
                const startTs = parseDate(interval.from);
                const endTs = parseDate(interval.to);

                if (startTs !== null && endTs !== null && endTs >= startTs) {
                  const clampedStartTs = Math.max(startTs, timelineData.timelineStartTs);
                  const clampedEndTs = Math.min(endTs, timelineData.timelineEndTs);

                  if (clampedEndTs < clampedStartTs) return null;

                  const startOffset = clampedStartTs - timelineData.timelineStartTs;
                  const intervalDuration = clampedEndTs - clampedStartTs;

                  const leftPercent = (startOffset / timelineData.totalTimelineDuration) * 100;
                  let widthPercent = (intervalDuration / timelineData.totalTimelineDuration) * 100;

                  if (widthPercent > 0 && widthPercent < 0.5) widthPercent = 0.5;
                  if (widthPercent <= 0) return null;

                  const verticalOffset = (index % 4) * 6;

                  return (
                    <div
                      key={interval.id}
                      className={`timeline-interval-block ${interval.monitoring ? 'monitoring' : ''}`}
                      style={{
                        left: `${leftPercent}%`,
                        width: `${widthPercent}%`,
                        bottom: `${5 + verticalOffset}px`,
                        zIndex: index,
                      }}
                      title={`Interval ${index + 1}: ${interval.from} to ${interval.to}${interval.monitoring ? ' (Monitoring)' : ''}`}
                    >{`Interval ${index + 1}`}</div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DynamicTimeSelector;